import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, ShieldCheck, ArrowLeft } from 'lucide-react';
import {
  useVerifyRegistrationMutation,
  useVerifyLoginMutation,
  useVerifyLogin2FAMutation,
  useResendOtpMutation,
} from '../../store/api/authApi';
import { clearPendingAuth } from '../../store/slices/authSlice';
import AuthShell, { AuthField, AuthAlert } from './AuthShell';
import OTPInput from '../common/OTPInput';
import useTimer from '../../hooks/useTimer';

const VerifyOTP = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingEmail, otpType } = useSelector((state) => state.auth);
  const verificationSuccessRef = useRef(false);

  // "Trust this device": skip the email OTP on this device for the chosen window.
  const [trustDevice, setTrustDevice] = useState(false);
  const [trustDays, setTrustDays] = useState(15);

  const [verifyRegistration, { isLoading: isVerifyingReg }] = useVerifyRegistrationMutation();
  const [verifyLogin, { isLoading: isVerifyingLogin }] = useVerifyLoginMutation();
  const [verifyLogin2FA, { isLoading: isVerifying2FA }] = useVerifyLogin2FAMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // SET-04: when the account has 2FA, verify-login returns a pending token and we
  // collect an authenticator (or backup) code before completing the login.
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState('');
  const { isActive, reset, formatTime } = useTimer(60);

  const isLoading = isVerifyingReg || isVerifyingLogin || isVerifying2FA;

  useEffect(() => {
    if (!pendingEmail || !otpType) {
      if (!verificationSuccessRef.current) {
        navigate('/login');
      }
    }
  }, [pendingEmail, otpType, navigate]);

  // Trusted device with 2FA: Login skipped the email OTP and sent us straight
  // to the authenticator step with a pending token.
  useEffect(() => {
    if (location.state?.pendingToken) {
      setPendingToken(location.state.pendingToken);
      setTwoFactorStep(true);
    }
  }, [location.state]);

  const handleOTPComplete = async (otp) => {
    setError('');
    setSuccess('');

    try {
      if (otpType === 'registration') {
        await verifyRegistration({ email: pendingEmail, otp }).unwrap();
      } else if (otpType === 'login') {
        const res = await verifyLogin({ email: pendingEmail, otp, trustDevice, trustDays }).unwrap();
        // SET-04: 2FA-enabled accounts must complete an authenticator step.
        if (res?.data?.twoFactorRequired) {
          setPendingToken(res.data.pendingToken);
          setTwoFactorStep(true);
          return;
        }
      }

      verificationSuccessRef.current = true;
      dispatch(clearPendingAuth());
      // Resume onboarding; the /onboarding index redirects completed merchants
      // straight to the dashboard.
      navigate('/onboarding');
    } catch (err) {
      setError(err.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  // SET-04: complete the login with a TOTP or backup code.
  const handle2FAVerify = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (!code.trim()) {
      setError('Enter your authenticator or backup code');
      return;
    }
    try {
      await verifyLogin2FA({ pendingToken, code: code.trim(), trustDevice, trustDays }).unwrap();
      verificationSuccessRef.current = true;
      dispatch(clearPendingAuth());
      navigate('/onboarding');
    } catch (err) {
      setError(err.data?.message || 'Invalid code. Please try again.');
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccess('');

    try {
      await resendOtp({ email: pendingEmail, type: otpType }).unwrap();
      setSuccess('OTP sent successfully.');
      reset();
    } catch (err) {
      setError(err.data?.message || 'Failed to resend OTP. Please try again.');
    }
  };

  const handleBack = () => {
    dispatch(clearPendingAuth());
    navigate(otpType === 'registration' ? '/register' : '/login');
  };

  // Offered on both steps: it governs this device, not this code.
  const trustControl = (
    <label className="mp-auth-trust" htmlFor="trust-device">
      <input
        type="checkbox"
        id="trust-device"
        checked={trustDevice}
        onChange={(e) => setTrustDevice(e.target.checked)}
      />
      <span>Trust this device and skip the code next time</span>
      {trustDevice && (
        <select
          value={trustDays}
          onChange={(e) => setTrustDays(Number(e.target.value))}
          aria-label="Trust duration"
          onClick={(e) => e.stopPropagation()}
        >
          <option value={15}>for 15 days</option>
          <option value={30}>for 30 days</option>
        </select>
      )}
    </label>
  );

  if (!pendingEmail) return null;

  if (twoFactorStep) {
    return (
      <AuthShell
        width="narrow"
        icon={<ShieldCheck size={20} />}
        eyebrow="Step 2 of 2"
        title="Two-factor verification"
        subtitle="Enter the 6-digit code from your authenticator app, or one of your backup codes."
      >
        {error && <AuthAlert>{error}</AuthAlert>}

        <form onSubmit={handle2FAVerify}>
          <div className="mp-auth-fields">
            <AuthField
              id="twofa-code"
              label="Authenticator or backup code"
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </div>

          {trustControl}

          <div className="mp-auth-actions">
            <button type="button" className="mp-auth-back" onClick={handleBack}>
              <ArrowLeft size={15} /> Back
            </button>
            <button type="submit" className="mp-auth-btn is-primary" disabled={isLoading}>
              {isLoading ? <span className="mp-auth-spin" /> : 'Verify and continue'}
            </button>
          </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      width="narrow"
      icon={<Mail size={20} />}
      eyebrow="Verify your email"
      title="Check your email"
      subtitle={<>We sent a 6-digit code to <strong>{pendingEmail}</strong></>}
    >
      {error && <AuthAlert>{error}</AuthAlert>}
      {success && <AuthAlert tone="ok">{success}</AuthAlert>}

      <OTPInput onComplete={handleOTPComplete} disabled={isLoading} />

      {trustControl}

      <p className="mp-auth-resend">
        {isActive ? (
          <>Did not receive it? Resend in <span className="mp-auth-timer">{formatTime()}</span></>
        ) : (
          <>
            Did not receive it?{' '}
            <button
              type="button"
              className="mp-auth-link"
              onClick={handleResendOTP}
              disabled={isResending}
            >
              {isResending ? 'Sending…' : 'Resend code'}
            </button>
          </>
        )}
      </p>

      {/* No submit button: OTPInput fires handleOTPComplete on the sixth digit,
          so a Verify button would either duplicate that or sit dead. The old
          page had one wired to nothing. */}
      <div className="mp-auth-actions">
        <button type="button" className="mp-auth-back" onClick={handleBack}>
          <ArrowLeft size={15} /> Back
        </button>
        {isLoading && <span className="mp-auth-timer">Verifying…</span>}
      </div>
    </AuthShell>
  );
};

export default VerifyOTP;
